import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Suspense, lazy, useEffect } from "react";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider } from "./context/AuthContext";
import { UsageProvider } from "./context/UsageContext";
import { CurrencyProvider } from "./hooks/useCurrency";
import { WhatsNewPopover } from "./components/layout/WhatsNewPopover";
import { UpgradeModal } from "./components/upgrade/UpgradeModal";
import { OnboardingGoalModal } from "./components/onboarding/OnboardingGoalModal";
import axios from "axios";

// Eagerly load the landing page (first paint)
import LandingPage from "./pages/LandingPage";

// Lazy load all other pages
const LoginPage = lazy(() => import("./pages/LoginPage"));
const RegisterPage = lazy(() => import("./pages/RegisterPage"));
const ForgotPasswordPage = lazy(() => import("./pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage"));
const ContentStudioPage = lazy(() => import("./pages/ContentStudioPage"));
const AdminDashboardPage = lazy(() => import("./pages/AdminDashboardPage"));
const AuthCallbackPage = lazy(() => import("./pages/AuthCallbackPage"));
const SuccessPage = lazy(() => import("./pages/SuccessPage"));
const GalleryPage = lazy(() => import("./pages/GalleryPage"));
const ProfilePage = lazy(() => import("./pages/ProfilePage"));
const EducationHubPage = lazy(() => import("./pages/EducationHubPage"));
const EducationModulePage = lazy(() => import("./pages/EducationModulePage"));
const CreatorAnalyticsPage = lazy(() => import("./pages/CreatorAnalyticsPage"));
const BrandKitPage = lazy(() => import("./pages/BrandKitPage"));
const CampaignBuilderPage = lazy(() => import("./pages/CampaignBuilderPage"));
const BlogPage = lazy(() => import("./pages/BlogPage"));
const ExperimentsAdminPage = lazy(() => import("./pages/admin/ExperimentsAdminPage"));
const TrashPage = lazy(() => import("./pages/TrashPage"));
const CreatorsPage = lazy(() => import("./pages/CreatorsPage"));
const ShopifyMarketPage = lazy(() => import("./pages/ShopifyMarketPage").then(m => ({ default: m.ShopifyMarketPage })));
const CreditsPage = lazy(() => import("./pages/CreditsPage"));
const AddonsPage = lazy(() => import("./pages/AddonsPage"));
const ComingSoonPage = lazy(() => import("./pages/ComingSoonPage"));
const SEOLandingPage = lazy(() => import("./pages/SEOLandingPage"));
const ScoreYourAdPage = lazy(() => import("./pages/ScoreYourAdPage"));
const VerifyEmailPage = lazy(() => import("./pages/VerifyEmailPage"));
const OnboardingPage = lazy(() => import("./pages/OnboardingPage"));
const LegalPage = lazy(() => import("./pages/LegalPage"));
const NotFoundPage = lazy(() => import("./pages/NotFoundPage"));

axios.defaults.withCredentials = true;

// Global axios interceptor: any 403 with an "upgrade/limit/free plan" error
// from the API fires the upgrade modal so we never silently leave a user
// staring at a toast saying "video generation not available".
let _upgradeInterceptorInstalled = false;
function installUpgradeInterceptor() {
  if (_upgradeInterceptorInstalled) return;
  _upgradeInterceptorInstalled = true;
  axios.interceptors.response.use(
    (r) => r,
    (error) => {
      const detail = error?.response?.data?.detail || "";
      const lower = (typeof detail === "string" ? detail : "").toLowerCase();
      if (error?.response?.status === 402) {
        window.dispatchEvent(new CustomEvent("lumora:upgrade-required", {
          detail: { reason: "credits", message: detail }
        }));
      }
      if (error?.response?.status === 403 && (lower.includes("limit") || lower.includes("free plan"))) {
        let reason = "generic";
        if (lower.includes("video") && lower.includes("free plan")) reason = "video-on-free";
        else if (lower.includes("image")) reason = "image-limit";
        else if (lower.includes("video")) reason = "video-limit";
        window.dispatchEvent(new CustomEvent("lumora:upgrade-required", {
          detail: { reason, message: detail }
        }));
      }
      return Promise.reject(error);
    }
  );
}

function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

function AppRouter() {
  const location = useLocation();
  useEffect(() => {
    if (location.pathname === "/" && location.hash && !location.hash.includes("session_id=")) {
      const id = location.hash.slice(1);
      const t = setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [location.pathname, location.hash]);
  // CRITICAL: Detect session_id synchronously during render (not in useEffect)
  // This prevents race conditions where other components redirect before AuthCallback processes
  if (location.hash?.includes('session_id=')) {
    return (
      <Suspense fallback={<PageLoader />}>
        <AuthCallbackPage />
      </Suspense>
    );
  }
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/onboarding" element={<OnboardingPage />} />
        <Route path="/auth/callback" element={<AuthCallbackPage />} />
        <Route path="/studio" element={<ContentStudioPage />} />
        <Route path="/gallery" element={<GalleryPage />} />
        <Route path="/gallery/trash" element={<TrashPage />} />
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/education" element={<EducationHubPage />} />
        <Route path="/education/:moduleId" element={<EducationModulePage />} />
        <Route path="/analytics" element={<CreatorAnalyticsPage />} />
        <Route path="/brand-kit" element={<BrandKitPage />} />
        <Route path="/campaigns" element={<CampaignBuilderPage />} />
        <Route path="/admin" element={<AdminDashboardPage />} />
        <Route path="/admin/experiments" element={<ExperimentsAdminPage />} />
        <Route path="/success" element={<SuccessPage />} />
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/blog/:slug" element={<BlogPage />} />
        <Route path="/creators" element={<CreatorsPage />} />
        <Route path="/fanvue" element={<CreatorsPage />} />
        <Route path="/shopify-australia" element={<ShopifyMarketPage market="au" />} />
        <Route path="/shopify-usa" element={<ShopifyMarketPage market="us" />} />
        <Route path="/credits" element={<CreditsPage />} />
        <Route path="/credits/success" element={<CreditsPage />} />
        <Route path="/addons" element={<AddonsPage />} />
        <Route path="/addons/success" element={<AddonsPage />} />
        <Route path="/team" element={<ComingSoonPage slug="team" urlSlug="team" />} />
        <Route path="/white-label" element={<ComingSoonPage slug="whitelabel" urlSlug="white-label" />} />
        <Route path="/developer-api" element={<ComingSoonPage slug="api" urlSlug="developer-api" />} />
        <Route path="/analytics-roas" element={<ComingSoonPage slug="roas" urlSlug="analytics-roas" />} />

        {/* Pricing lives on the home page; old competitor pages redirect there */}
        <Route path="/pricing" element={<Navigate to="/#pricing" replace />} />
        <Route path="/creatify-alternative" element={<Navigate to="/pricing" replace />} />
        <Route path="/arcads-alternative" element={<Navigate to="/pricing" replace />} />
        <Route path="/zeely-alternative" element={<Navigate to="/pricing" replace />} />
        <Route path="/heygen-alternative" element={<Navigate to="/pricing" replace />} />
        <Route path="/makeugc-alternative" element={<Navigate to="/pricing" replace />} />
        <Route path="/ai-ugc-generator"      element={<SEOLandingPage slug="ai-ugc-generator" />} />
        <Route path="/ai-ad-generator"       element={<SEOLandingPage slug="ai-ad-generator" />} />
        <Route path="/shopify-ad-generator"  element={<SEOLandingPage slug="shopify-ad-generator" />} />
        <Route path="/tiktok-ad-generator"   element={<SEOLandingPage slug="tiktok-ad-generator" />} />
        <Route path="/ecommerce-video-ads"   element={<SEOLandingPage slug="ecommerce-video-ads" />} />

        {/* Public lead-magnet — anonymous Creative Score */}
        <Route path="/score-your-ad" element={<ScoreYourAdPage />} />
        <Route path="/roi-calculator" element={<Navigate to="/score-your-ad?tab=roi" replace />} />

        {/* Legal */}
        <Route path="/privacy" element={<LegalPage slug="privacy" />} />
        <Route path="/terms" element={<LegalPage slug="terms" />} />
        <Route path="/refund-policy" element={<LegalPage slug="refund-policy" />} />

        <Route path="/404" element={<NotFoundPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  useEffect(() => { installUpgradeInterceptor(); }, []);
  return (
    <BrowserRouter>
      <AuthProvider>
        <UsageProvider>
          <CurrencyProvider>
            <div className="min-h-screen bg-[#030014] text-white">
              <AppRouter />
              <WhatsNewPopover />
              <UpgradeModal />
              <OnboardingGoalModal />
              <Toaster position="top-center" theme="dark" richColors />
            </div>
          </CurrencyProvider>
        </UsageProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
